import { Link, createFileRoute } from "@tanstack/react-router";

import { Route as AuthLoginRoute } from "@/routes/_app/login/_layout.index";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Loader2 } from "lucide-react";
import { Logo } from "../components/ui/logo";
import { buttonVariants } from "@/components/ui/button-util";
import { cn } from "@/utils/misc";
import { useAccount } from "@/state/useAccount";
import { useConvexAuth } from "@convex-dev/react-query";
import { useState } from "react";

type Quote = {
  text: string;
  author: string;
  image?: string;
};

type ContentType = {
  header: [string, string];
  subheader: string;
  description: string[];
  quote: Quote;
  ctaText: string;
  ctaLink: string;
};

export const Route = createFileRoute("/")({
  beforeLoad: () => {},
  component: Index,
});

function Index() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const { appId, creatorId } = useAccount();

  const [view, setView] = useState<"creators" | "brands">("creators");

  const gradientStyles = {
    creators: "from-red-900/30 via-slate-900/25 to-red-950/30",
    brands: "from-blue-900/30 via-slate-900/25 to-blue-950/30",
  };

  const content: Record<"creators" | "brands", ContentType> = {
    creators: {
      header: ["Become a", "Creator-in-Residence"],
      subheader: "Learn, earn and turbocharge your path to Creator success",
      description: [
        "Imagine getting paid to learn from the best creators in the world.",
        "Create content for brands you love, while being mentored by Ali Abdaal's team.",
        "Earn $1,000s in your first month while mastering the art of content creation.",
      ],
      quote: {
        text: "The opportunity I wish I had when I started my Youtube channel",
        author: "Ali Abdaal",
        image:
          "https://encrypted-tbn3.gstatic.com/images?q=tbn:ANd9GcRz7ep1CUqk2FqXBjs40IH75mXX3U-hm1563xYGpvx-D1wgkF496LKQVak0R27wdljWeerrcK58YqQEbvQYutClJQ",
      },
      ctaText: "Get Started",
      ctaLink: AuthLoginRoute.fullPath,
    },
    brands: {
      header: ["Grow your business", "faster with UGC"],
      subheader: "Your Brand × World-Class Creators",
      description: [
        "Stop wasting time searching for the right creators.",
        "Get matched with pre-vetted talent who understand your brand.",
        "Scale your content production with a managed service that just works.",
      ],
      quote: {
        text: "The growth lever I wish I knew about 12 months ago",
        author: "Pablo S, 3x exited founder",
        image:
          "https://pbs.twimg.com/profile_images/1644359347818881025/2X5513rh_400x400.jpg",
      },
      ctaText: "Join Waitlist",
      ctaLink:
        "https://docs.google.com/forms/d/e/1FAIpQLSeRk8Dw7XfksSn7TTkpvv1pseeT_xwTLdF-Pmn4_h1TEtZgVw/viewform?usp=header",
    },
  };

  const activeContent = content[view];

  return (
    <div className="relative min-h-screen w-full bg-card flex flex-col">
      {/* Navigation */}
      <div className="mx-auto flex w-full max-w-screen-lg items-center justify-between p-6 py-3">
        <Link to="/" className="flex h-10 items-center gap-1">
          <Logo />
        </Link>
        {/* <button
          onClick={() => {
            localStorage.clear();
            window.location.reload();
          }}
          className="px-4 py-2 text-sm font-medium rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
        >
          Clear Storage
        </button> */}
        {view === "creators" && (
          <Link
            to={
              isAuthenticated && (appId || creatorId)
                ? DashboardRoute.fullPath
                : AuthLoginRoute.fullPath
            }
            className={buttonVariants({ size: "sm" })}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="animate-spin w-16 h-4" />}
            {!isLoading &&
              isAuthenticated &&
              (appId || creatorId) &&
              "Dashboard"}
            {!isLoading &&
              isAuthenticated &&
              !(appId || creatorId) &&
              "Onboarding"}
            {!isLoading && !isAuthenticated && activeContent.ctaText}
          </Link>
        )}
      </div>

      {/* View Switch */}
      <div className="flex justify-center">
        <div className="flex rounded-full bg-secondary p-1">
          <button
            onClick={() => setView("creators")}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-full transition-colors",
              view === "creators"
                ? "bg-primary text-primary-foreground"
                : "hover:bg-primary/10",
            )}
          >
            Creators
          </button>
          <button
            onClick={() => setView("brands")}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-full transition-colors",
              view === "brands"
                ? "bg-primary text-primary-foreground"
                : "hover:bg-primary/10",
            )}
          >
            Brands
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto w-full max-w-screen-lg px-6 flex-grow">
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-12 md:p-24">
          <h1 className="text-center text-6xl font-bold leading-tight md:text-7xl lg:leading-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
            {activeContent.header[0]}
            <br />
            {activeContent.header[1]}
          </h1>
          <p className="max-w-screen-md text-center text-xl !leading-normal text-muted-foreground md:text-2xl font-light">
            {activeContent.subheader}
          </p>
          <div className="mt-8 space-y-4 text-center">
            {activeContent.description.map((line, index) => (
              <p
                key={index}
                className="text-lg text-primary/80 leading-relaxed"
              >
                {line}
              </p>
            ))}
          </div>
          {/* <div className="mt-12 text-center flex items-center gap-8">
            <div className="flex-1 text-right">
              <blockquote className="italic text-2xl text-primary/80 font-serif">
                "{activeContent.quote.text}"
              </blockquote>
              <cite className="mt-4 block text-sm text-primary/60">
                - {activeContent.quote.author}
              </cite>
            </div>
            {activeContent.quote.image && (
              <div className="flex-shrink-0">
                <img
                  src={activeContent.quote.image}
                  alt={activeContent.quote.author}
                  className="w-24 h-24 rounded-full object-cover"
                />
              </div>
            )}
          </div> */}
          <div className="mt-12">
            <a
              href={activeContent.ctaLink}
              className={cn(
                buttonVariants({ size: "lg" }),
                "px-12 py-6 text-lg font-semibold tracking-wide rounded-full transition-all duration-300 hover:scale-105",
              )}
              target={view === "brands" ? "_blank" : undefined}
              rel={view === "brands" ? "noreferrer" : undefined}
            >
              {activeContent.ctaText}
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-auto relative z-20 border-t border-border/50 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/75">
        <div className="mx-auto max-w-screen-lg px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-primary">
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-primary">
              Terms of Service
            </Link>
          </div>
          <div className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} CreatorGrid. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Background */}
      <div className="fixed left-0 top-0 z-10 h-full w-full pointer-events-none">
        <div
          className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-700 ease-in-out ${gradientStyles.creators}`}
          style={{ opacity: view === "creators" ? 1 : 0 }}
        />
        <div
          className={`absolute inset-0 bg-gradient-to-br transition-opacity duration-700 ease-in-out ${gradientStyles.brands}`}
          style={{ opacity: view === "brands" ? 1 : 0 }}
        />
      </div>
      {/* <div className="fixed bottom-0 h-screen w-screen bg-gradient-to-t from-[hsl(var(--card))] to-transparent" /> */}
    </div>
  );
}
